import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import { KeyboardArrowDownIcon } from 'icons/materialIcons';
import { Bar } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import { getCardById } from 'apis/cardApi';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const METRIC_OPTIONS = [
    { value: 'leads', label: 'Leads' },
    { value: 'views', label: 'Views' }
];

const TopPerformers = ({ leadsData, viewsData, members = [] }) => {
    const [selectedMetric, setSelectedMetric] = useState('leads');
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [cardsMap, setCardsMap] = useState({}); // { cardId: { name, profilePicture } }
    const [loadingCards, setLoadingCards] = useState(new Set());

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMetricSelect = (metric) => {
        setSelectedMetric(metric);
        handleClose();
    };

    // Fetch card information for cardIds
    useEffect(() => {
        const fetchCards = async () => {
            // Get all unique cardIds from perCard data
            const perCardData = selectedMetric === 'leads'
                ? (leadsData?.perCard || {})
                : (viewsData?.perCard || {});

            const cardIds = Object.keys(perCardData).filter(cardId =>
                !cardId.startsWith('user_') && // Exclude fallback user keys
                !cardsMap[cardId] &&
                !loadingCards.has(cardId)
            );

            if (cardIds.length === 0) return;

            // Mark as loading
            setLoadingCards(prev => new Set([...prev, ...cardIds]));

            // Fetch all cards in parallel
            const fetchPromises = cardIds.map(async (cardId) => {
                try {
                    const cardData = await getCardById(cardId);
                    if (cardData && cardData.card) {
                        return {
                            cardId,
                            name: cardData.card.cardName ||
                                cardData.card.contact_info?.fullName ||
                                'Card',
                            profilePicture: cardData.card.profilePictureUrl || null
                        };
                    }
                    return null;
                } catch (error) {
                    console.error(`Error fetching card ${cardId}:`, error);
                    return {
                        cardId,
                        name: 'Card',
                        profilePicture: null
                    };
                }
            });

            try {
                const results = await Promise.all(fetchPromises);
                const newCardsMap = { ...cardsMap };
                results.forEach(result => {
                    if (result) {
                        newCardsMap[result.cardId] = {
                            name: result.name,
                            profilePicture: result.profilePicture
                        };
                    }
                });
                setCardsMap(newCardsMap);
            } catch (error) {
                console.error('Error fetching cards:', error);
            } finally {
                // Remove from loading set
                setLoadingCards(prev => {
                    const newSet = new Set(prev);
                    cardIds.forEach(id => newSet.delete(id));
                    return newSet;
                });
            }
        };

        fetchCards();
    }, [selectedMetric, leadsData?.perCard, viewsData?.perCard]);

    // Process data to get performance per card
    const performanceData = useMemo(() => {
        // Get per-card breakdown from API response
        const perCardData = selectedMetric === 'leads'
            ? (leadsData?.perCard || {})
            : (viewsData?.perCard || {});

        // Convert to array format
        const cardPerformance = Object.entries(perCardData).map(([cardId, count]) => {
            // Skip fallback user keys
            if (cardId.startsWith('user_')) {
                return null;
            }

            const cardInfo = cardsMap[cardId];
            return {
                cardId,
                count,
                name: cardInfo?.name || 'Card',
                profilePicture: cardInfo?.profilePicture || null
            };
        }).filter(Boolean); // Remove null entries

        // Sort by count (descending - highest first)
        return cardPerformance
            .filter(item => item.count > 0) // Only show cards with data
            .sort((a, b) => b.count - a.count);
    }, [selectedMetric, leadsData?.perCard, viewsData?.perCard, cardsMap]);

    // Generate color palette for bars - using #abf5e3, #d2cafd and similar shades
    const colorPalette = [
        'rgba(171, 245, 227, 0.8)',  // #abf5e3 - Mint/Cyan Green
        'rgba(210, 202, 253, 0.8)',  // #d2cafd - Lavender/Purple
        'rgba(171, 220, 245, 0.8)',  // #abdcf5 - Light Blue
        'rgba(202, 210, 253, 0.8)',  // #cad2fd - Light Purple
        'rgba(171, 245, 210, 0.8)',  // #abf5d2 - Light Mint
        'rgba(245, 202, 253, 0.8)',  // #f5cafd - Light Pink/Purple
        'rgba(202, 245, 227, 0.8)',  // #caf5e3 - Soft Mint
        'rgba(220, 210, 253, 0.8)',  // #dcd2fd - Soft Lavender
        'rgba(171, 235, 245, 0.8)',  // #abebf5 - Sky Blue
        'rgba(235, 202, 253, 0.8)'   // #ebcafd - Soft Purple
    ];

    const borderColorPalette = [
        '#abf5e3',  // Mint/Cyan Green
        '#d2cafd',  // Lavender/Purple
        '#abdcf5',  // Light Blue
        '#cad2fd',  // Light Purple
        '#abf5d2',  // Light Mint
        '#f5cafd',  // Light Pink/Purple
        '#caf5e3',  // Soft Mint
        '#dcd2fd',  // Soft Lavender
        '#abebf5',  // Sky Blue
        '#ebcafd'   // Soft Purple
    ];

    // Prepare chart data
    const chartData = useMemo(() => {
        if (performanceData.length === 0) {
            return {
                labels: [],
                datasets: [{
                    label: selectedMetric === 'leads' ? 'Leads' : 'Views',
                    data: [],
                    backgroundColor: 'rgba(41, 174, 248, 0.8)',
                    borderColor: '#29AEF8',
                    borderWidth: 1
                }]
            };
        }

        return {
            labels: performanceData.map(() => ''), // Empty labels - we'll show profile pictures and names below instead
            datasets: [{
                label: selectedMetric === 'leads' ? 'Leads' : 'Views',
                data: performanceData.map(item => item.count),
                backgroundColor: performanceData.map((_, index) => colorPalette[index % colorPalette.length]),
                borderColor: performanceData.map((_, index) => borderColorPalette[index % borderColorPalette.length]),
                borderWidth: 1,
                borderRadius: 4,
                barThickness: 70, // Increased bar width
                maxBarThickness: 80 // Increased maximum bar width
            }]
        };
    }, [performanceData, selectedMetric]);


    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'x', // Vertical bars (default)
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        return `${selectedMetric === 'leads' ? 'Leads' : 'Views'}: ${context.parsed.y}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    display: false, // Hide default labels - we'll show profile pictures and names instead
                    maxRotation: 45,
                    minRotation: 0,
                    font: {
                        size: 11
                    }
                },
                categoryPercentage: 0.9, // Increased - Controls the percentage of the available width each category should take
                barPercentage: 1.0 // Increased - Controls the percentage of the available width each bar should take within its category
            },
            y: {
                beginAtZero: true,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)'
                },
                ticks: {
                    stepSize: 1,
                    precision: 0,
                    font: {
                        size: 11
                    }
                }
            }
        }
    };

    // Render dropdown
    const renderDropdown = () => (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    gap: '5px',
                    '&:hover': {
                        opacity: 0.8
                    }
                }}
                onClick={handleClick}
            >
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#333333'
                    }}
                >
                    Top Performers: {METRIC_OPTIONS.find(opt => opt.value === selectedMetric)?.label || 'Leads'}
                </Typography>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 16,
                        color: '#828282',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                    }}
                />
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        minWidth: '180px',
                        mt: 1
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                {METRIC_OPTIONS.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => handleMetricSelect(option.value)}
                        sx={{
                            fontSize: '14px',
                            fontWeight: selectedMetric === option.value ? 600 : 400,
                            color: selectedMetric === option.value ? '#29AEF8' : '#4F4F4F',
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: '#F7F7F7'
                            }
                        }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );

    return (
        <Box
            sx={{
                width: '100%',
                marginTop: '24px',
                borderRadius: '18px',
                backgroundColor: '#FFFFFF',
                padding: '32px 18px 26px 18px'
            }}
        >
            {/* Header with Dropdown */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '24px',
                    gap: '5px'
                }}
            >
                {renderDropdown()}
            </Box>

            {/* Chart Container */}
            {performanceData.length > 0 ? (
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ height: '350px', position: 'relative', marginBottom: '50px' }}>
                        <Bar data={chartData} options={chartOptions} />
                    </Box>

                    {/* Profile Pictures and Names aligned with x-axis labels */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'flex-start',
                            paddingX: '20px',
                            marginTop: '-50px'
                        }}
                    >
                        {performanceData.map((item, index) => (
                            <Box
                                key={item.cardId || index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flex: performanceData.length > 5 ? '0 0 auto' : '1',
                                    minWidth: performanceData.length > 5 ? '60px' : 'auto',
                                    maxWidth: '100px'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: '2px solid #FFFFFF',
                                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
                                        backgroundColor: '#E0E0E0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '6px'
                                    }}
                                >
                                    {item.profilePicture ? (
                                        <img
                                            src={item.profilePicture}
                                            alt={item.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                if (e.target.nextSibling) {
                                                    e.target.nextSibling.style.display = 'flex';
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: item.profilePicture ? 'none' : 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#29AEF8',
                                            color: '#FFFFFF',
                                            fontSize: '16px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {item.name.charAt(0).toUpperCase()}
                                    </Box>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        color: '#333333',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%',
                                        fontWeight: 500
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '400px',
                        color: '#999999'
                    }}
                >
                    <Typography>No {selectedMetric} data available</Typography>
                </Box>
            )}
        </Box>
    );
};

TopPerformers.propTypes = {
    leadsData: PropTypes.object,
    viewsData: PropTypes.object,
    members: PropTypes.array
};

export default TopPerformers;

